// RraRequestFields — "what are you asking us to change", rendered by BOTH the public requester
// form (RraFormPage) and the console's staff fill-in (AccommodationsPage IntakePanel) so they
// cannot drift into asking different things (RRA v2.1, B3).
//
//   RequestStatementFields — at least one option is required:
//     • the animal option, on an animal item only, pre-checked ("we've checked it for you"), with
//       the fixed no-charges line under it while it is checked (the waiver is a consequence of the
//       animal, not a separate ask);
//     • "A change to another rule, or to how you do things" → a one-line input;
//     • "A physical change to the home or the building" → a one-line input.
//   The group is a <fieldset><legend>; each text input has a <label htmlFor> (B10).
//
// The optional "how this would help" box is a sibling component (components/rra/HelpBox.tsx).
// There is no field for a diagnosis anywhere here, and nothing ever shows an id. The words come
// from lib/rraCopy.ts.

import {
  NO_ANIMAL_CHARGES_LINE,
  OTHER_RULE_INPUT,
  OTHER_RULE_LABEL,
  PHYSICAL_CHANGE_INPUT,
  PHYSICAL_CHANGE_LABEL,
  allowAnimalLabel,
  type RraCopySubject,
} from '@/lib/rraCopy';
import { REQUEST_TEXT_MAX, type RequestDraft } from '@/lib/rraRequestDraft';

const choiceStyle = { display: 'flex', alignItems: 'flex-start' } as const;
const boxStyle = { flex: 'none', marginTop: '0.2em' } as const;
const errorStyle = { color: 'var(--danger)', fontSize: 'var(--text-sm)', margin: 0 } as const;

export function RequestStatementFields(props: {
  /** Prefix for the inputs' ids (unique per item on the page). */
  idPrefix: string;
  /** The fieldset's own id (the "Still needed" list links to it). */
  fieldId?: string;
  /** The legend: "What you're asking us to change" (the staff fill-in uses its own words). */
  heading: string;
  /** The helper under the legend (B3); omitted on the staff fill-in. */
  helper?: string | undefined;
  value: RequestDraft;
  /** Whose voice the animal option is in (B2). */
  subject: RraCopySubject;
  /** The animal option appears only on an animal item (B3). */
  isAnimal: boolean;
  disabled?: boolean;
  onChange: (patch: Partial<RequestDraft>) => void;
  /** Called when the requester touches the group (a tick, or leaving a text box). */
  onTouch?: () => void;
  /** Inline message under the group (e.g. "Tell us what you'd like us to change."). */
  error?: string | undefined;
}): React.JSX.Element {
  const { idPrefix, value: v, disabled = false, onChange, onTouch } = props;
  const id = (name: string): string => `${idPrefix}-${name}`;
  const errorId = id('error');
  const change = (patch: Partial<RequestDraft>): void => {
    onChange(patch);
    onTouch?.();
  };

  return (
    <fieldset
      {...(props.fieldId ? { id: props.fieldId } : {})}
      style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
      {...(props.error ? { 'aria-describedby': errorId } : {})}
    >
      <legend style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--weight-medium)', padding: 0 }}>
        {props.heading}
      </legend>
      <div className="stack" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
        {props.helper ? <p style={{ color: 'var(--fg-muted)', margin: 0 }}>{props.helper}</p> : null}
        <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 'var(--space-3)' }}>
          {props.isAnimal ? (
            <li>
              <label className="choice" style={choiceStyle}>
                <input
                  type="checkbox"
                  checked={v.allowAnimal}
                  disabled={disabled}
                  onChange={(e) => change({ allowAnimal: e.target.checked })}
                  style={boxStyle}
                />
                <span>{allowAnimalLabel(props.subject)}</span>
              </label>
              {v.allowAnimal ? (
                <p style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', margin: 'var(--space-1) 0 0 var(--space-6)' }}>
                  {NO_ANIMAL_CHARGES_LINE}
                </p>
              ) : null}
            </li>
          ) : null}
          <li>
            <label className="choice" style={choiceStyle}>
              <input
                type="checkbox"
                checked={v.otherRuleOn}
                disabled={disabled}
                onChange={(e) => change({ otherRuleOn: e.target.checked })}
                style={boxStyle}
              />
              <span>{OTHER_RULE_LABEL}</span>
            </label>
            {v.otherRuleOn ? (
              <div className="field" style={{ marginTop: 'var(--space-2)', marginLeft: 'var(--space-6)' }}>
                <label htmlFor={id('otherRule')}>{OTHER_RULE_INPUT}</label>
                <input
                  id={id('otherRule')}
                  className="input"
                  maxLength={REQUEST_TEXT_MAX}
                  value={v.otherRule}
                  disabled={disabled}
                  onChange={(e) => onChange({ otherRule: e.target.value })}
                  onBlur={() => onTouch?.()}
                />
              </div>
            ) : null}
          </li>
          <li>
            <label className="choice" style={choiceStyle}>
              <input
                type="checkbox"
                checked={v.physicalChangeOn}
                disabled={disabled}
                onChange={(e) => change({ physicalChangeOn: e.target.checked })}
                style={boxStyle}
              />
              <span>{PHYSICAL_CHANGE_LABEL}</span>
            </label>
            {v.physicalChangeOn ? (
              <div className="field" style={{ marginTop: 'var(--space-2)', marginLeft: 'var(--space-6)' }}>
                <label htmlFor={id('physicalChange')}>{PHYSICAL_CHANGE_INPUT}</label>
                <input
                  id={id('physicalChange')}
                  className="input"
                  maxLength={REQUEST_TEXT_MAX}
                  value={v.physicalChange}
                  disabled={disabled}
                  onChange={(e) => onChange({ physicalChange: e.target.value })}
                  onBlur={() => onTouch?.()}
                />
              </div>
            ) : null}
          </li>
        </ul>
        {props.error ? (
          <p id={errorId} role="alert" style={errorStyle}>
            {props.error}
          </p>
        ) : null}
      </div>
    </fieldset>
  );
}
